import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {Col, Modal, Row, Spinner, Form as BSForm, Popover, OverlayTrigger} from 'react-bootstrap'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {ContactForm, ContactFormInput} from '../../../../../../services/cellcast.v2.types'
import {
  useCreateContactFormMutation,
  useUpdateContactFormMutation,
} from '../../../../../../services/cellcast.v2'
import {ErrorMessage, Field, Form, Formik, FormikHelpers, FormikProps} from 'formik'
import {array, object, string} from 'yup'
import Select, {Options, components, MenuListProps} from 'react-select'
import {SelectOption} from '../../../../../types'
import {useLazyGetGroupQuery} from '../../../../../../services/cellcast'
import {useListTag} from '../../Components/UploadContactModal/SetOptions/useListTagHooks'
import {Tag} from '../../../../../../services/cellcast.type'
import {toast} from 'react-toastify'
import {ReactSortable} from 'react-sortablejs'
import CreateTagModal, {
  CreateTagModalMode,
} from '../../Components/UploadContactModal/SetOptions/CreateTagModal'
import './WriteModal.css'

const formSchema = object().shape({
  name: string().required('Name required'),
  description: string(),
  fields: array().min(1, 'Fields are required'),
})

export type WriteListModalProps = {
  show: boolean
  contactForm?: ContactForm
  handleClose: () => void
  callbackFn: () => void
}

const WriteModal: FC<WriteListModalProps> = (props) => {
  const {contactForm, show, handleClose, callbackFn} = props

  const [fetchGroup, {isLoading: isGroupLoading}] = useLazyGetGroupQuery()
  const {fetchTagsData, data: tagsData} = useListTag()

  const [create, {isLoading: createLoading}] = useCreateContactFormMutation()
  const [update, {isLoading: updateLoading}] = useUpdateContactFormMutation()
  const formRef = useRef<FormikProps<ContactFormInput>>(null)

  const [showCreateTagModal, setShowCreateTagModal] = useState(false)

  const [localForm, setLocalForm] = useState<ContactForm>({
    name: '',
    description: '',
    tags: [],
    fields: [
      {
        name: 'Mobile Number',
        type: 'text',
      },
    ],
    isActive: true,
    isUseDailyCaptureData: false,
    isAutoDeleteContact: false,
    isMarketingMessageOptIn: true,
    marketingMessageOptin: '',
    _id: '',
    logo: '',
    redirectUrl: '',
  })

  const [fieldOptions, setFieldOptions] = useState<Options<SelectOption<string>>>([
    {
      label: 'Select Field',
      value: '',
    },
    {
      label: 'Mobile Number',
      value: 'Mobile Number',
    },
  ])

  const tags = tagsData?.data
    ? tagsData.data?.filter((el: Tag) => !el.type || el?.type === 'tag')
    : []
  const toOptions = (tags: Tag[]) => {
    return tags.map<SelectOption<string>>((el) => ({
      label: el.name,
      value: el._id,
    }))
  }

  const MenuList = (props: MenuListProps<any>) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <div
          style={{
            width: '100%',
            display: 'flex',
            gap: '6px',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            borderTop: '1px solid #F4F5F7',
            paddingTop: '10px',
          }}
          onClick={() => {
            setShowCreateTagModal(true)
          }}
        >
          <i
            style={{
              color: '#7950F2',
              fontWeight: 'bold !important',
            }}
            className='bi bi-plus-lg'
          ></i>
          <p
            style={{
              color: '#7950F2',
              margin: 0,
              fontWeight: 600,
            }}
          >
            Create new
          </p>
        </div>
      </components.MenuList>
    )
  }

  useEffect(() => {
    if (show === true) {
      fetchGroup(null, true).then((res) => {
        if ('data' in res) {
          const fields = res.data?.data.fields
          const defaultFields = [
            {
              label: 'Select Field',
              value: '',
            },
            {
              label: 'Mobile Number',
              value: 'Mobile Number',
            },
          ]

          const options =
            fields?.map((field) => {
              return {
                label: field,
                value: field,
              }
            }) || []
          setFieldOptions([...defaultFields, ...options])
        }
      })

      fetchTagsData(null)
    }
  }, [show])

  useEffect(() => {
    if (!contactForm) {
      if (formRef.current) {
        formRef.current.resetForm()
      }
      setLocalForm({
        name: '',
        description: '',
        tags: [],
        fields: [
          {
            name: 'Mobile Number',
            type: 'text',
          },
        ],
        isActive: true,
        _id: '',
        isUseDailyCaptureData: false,
        isAutoDeleteContact: false,
        isMarketingMessageOptIn: true,
        marketingMessageOptin: '',
        logo: '',
        redirectUrl: '',
      })
    } else {
      if (formRef.current) {
        formRef.current.setValues(contactForm)
      }
      setLocalForm(contactForm)
    }
  }, [contactForm])

  const handleSubmit = useCallback(
    (value: ContactFormInput, action: FormikHelpers<ContactFormInput>) => {
      if (value.isActive === undefined) {
        value.isActive = true
      }

      if (value.isAutoDeleteContact === undefined || value.isAutoDeleteContact === null) {
        value.isAutoDeleteContact = false
      }

      if (value.isUseDailyCaptureData === undefined || value.isUseDailyCaptureData === null) {
        value.isUseDailyCaptureData = false
      }

      if (value.isMarketingMessageOptIn === undefined || value.isMarketingMessageOptIn === null) {
        value.isMarketingMessageOptIn = false
      }

      // build form data
      const formData = new FormData()

      // Append all non-file fields
      for (const [key, val] of Object.entries(value)) {
        if (key === 'logo') continue // We'll handle logo separately

        // For arrays/objects, stringify them
        if (Array.isArray(val) || typeof val === 'object') {
          formData.append(key, JSON.stringify(val))
        } else {
          formData.append(key, val as any)
        }
      }

      // Handle file input
      if (typeof value.logo === 'object') {
        formData.append('logo', value.logo) // Assuming `logo` is a File object
      }

      const submitAction =
        contactForm && contactForm._id
          ? update({id: contactForm._id, data: formData}) // formData
          : create(formData) // formData

      submitAction
        .then((res) => {
          if ('data' in res) {
            toast.success(res.data?.message)

            if (callbackFn) {
              callbackFn()
            }
          } else if ('error' in res) {
            const errObj = res.error as any
            if (errObj.data.message) {
              toast.error(errObj.data.message)
            } else {
              toast.error(res.error)
            }
          }
        })
        .catch((err) => {
          toast.error(err.error)
        })
        .finally(() => {
          action.setSubmitting(false)
        })
    },
    [show, contactForm]
  )

  return (
    <Modal
      id='kt_modal_create_app'
      tabIndex={1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-700px'
      className='vh-100'
      show={show}
      backdrop='static'
      onHide={handleClose}
    >
      <div className='scroll-x'>
        <div className='modal-header'>
          <h2 className='fs-2'>
            {contactForm && contactForm._id && contactForm._id.length > 0 ? 'Update' : 'Create'} New
            Form
          </h2>
          <div
            className='btn btn-icon btn-sm'
            onClick={() => {
              handleClose()
            }}
          >
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
          </div>
        </div>
        <div className='modal-body py-lg-5 px-lg-5 border-bottom'>
          <div className='mb-10 container'>
            <Formik<ContactFormInput>
              initialValues={localForm}
              onSubmit={handleSubmit}
              validationSchema={formSchema}
              innerRef={formRef}
            >
              {({values, setFieldValue, handleChange}) => (
                <Form>
                  <Row>
                    <Col md={12}>
                      <BSForm.Group>
                        <BSForm.Label htmlFor='name'>Logo</BSForm.Label>
                      </BSForm.Group>
                      <Row>
                        <div className='col-lg-8 mb-4'>
                          <div
                            className='image-input image-input-outline w-auto'
                            data-kt-image-input='true'
                          >
                            {typeof values.logo !== 'object' ? (
                              values.logo ? (
                                <img src={values.logo} alt='poster' width={125} height={125} />
                              ) : (
                                <span className='form-logo'>
                                  upload <br /> your logo
                                </span>
                              )
                            ) : (
                              <img
                                src={URL.createObjectURL(values.logo)}
                                alt='poster'
                                width={125}
                                height={125}
                              />
                            )}

                            <label
                              className='btn btn-icon btn-circle btn-color btn-active-color-primary w-25px h-25px bg-body shadow'
                              data-kt-image-input-action='change'
                              data-bs-toggle='tooltip'
                              data-bs-dismiss='click'
                              title='Change avatar'
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='16'
                                height='16'
                                fill='currentColor'
                                className='bi bi-pencil-fill'
                                viewBox='0 0 16 16'
                              >
                                <path d='M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z' />
                              </svg>
                              <input
                                type='file'
                                name='logo'
                                onChange={(event: any) =>
                                  setFieldValue('logo', event?.target.files[0])
                                }
                                accept='.png, .jpg, .jpeg'
                              />
                            </label>
                            {typeof values.logo === 'object' && (
                              <span
                                className='btn btn-icon btn-circle btn-color btn-active-color-primary w-25px h-25px bg-body shadow'
                                data-kt-image-input-action='remove'
                                data-bs-toggle='tooltip'
                                data-bs-dismiss='click'
                                title='Remove logo'
                                onClick={(event: any) => setFieldValue('logo', '')}
                              >
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='25'
                                  height='25'
                                  fill='currentColor'
                                  className='bi bi-x'
                                  viewBox='0 0 16 16'
                                >
                                  <path d='M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z' />
                                </svg>
                              </span>
                            )}
                          </div>
                          <div className='form-text mt-8'>Allowed file types: png, jpg, jpeg.</div>
                        </div>
                      </Row>
                      <ErrorMessage name='name' component='span' className='text-danger' />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <BSForm.Group>
                        <BSForm.Label htmlFor='name' className=' required'>
                          Name
                        </BSForm.Label>
                      </BSForm.Group>
                      <Field
                        id={'name'}
                        className={'form-control form-control-solid  mb-3'}
                        name='name'
                        type='text'
                        onChange={handleChange}
                        disabled={createLoading || updateLoading}
                      />
                      <ErrorMessage name='name' component='span' className='text-danger' />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <BSForm.Group>
                        <BSForm.Label htmlFor='description'>Description</BSForm.Label>
                        <Field
                          id={'description'}
                          className={'form-control  form-control-solid mb-3'}
                          name='description'
                          type='text'
                          onChange={handleChange}
                          disabled={createLoading || updateLoading}
                        />
                        <ErrorMessage name='description' component='span' className='text-danger' />
                      </BSForm.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <BSForm.Group>
                        <BSForm.Label htmlFor='fields' className='required'>
                          Fields
                        </BSForm.Label>
                        {values.fields.length > 0 && (
                          <Row>
                            <Col md={8}>
                              <ReactSortable
                                tag='ul'
                                list={values.fields.map((el, index) => {
                                  return {id: index + 1, name: el.name}
                                })}
                                setList={(evt) => {
                                  const newFields = evt.map((el) => {
                                    return {name: el.name, type: 'text'}
                                  })
                                  setFieldValue('fields', newFields)
                                }}
                                className='list-group'
                              >
                                {values.fields
                                  .map((el, index) => {
                                    return {id: index + 1, name: el.name}
                                  })
                                  .map((item, index) => (
                                    <li
                                      key={index}
                                      className='ms-5 list-group-item'
                                      style={{paddingLeft: 0}}
                                    >
                                      <div className='col-md-6 col-lg-11'>
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '-20px',
                                            transform: 'translateY(-50%)',
                                            padding: '0.60rem',
                                            cursor: 'grab',
                                            backgroundColor: '#F5F8FA',
                                            color: '#fffff',
                                            borderTopLeftRadius: '0.325rem',
                                            borderBottomLeftRadius: '0.325rem',
                                          }}
                                        >
                                          <i className='bi bi-grip-vertical drag-handle'></i>
                                        </div>
                                        <div key={index} className='input-delete'>
                                          <input
                                            value={item.name}
                                            type='text'
                                            placeholder={`Field ${item.id}`}
                                            disabled={true}
                                            name={'field'}
                                            className='form-control form-control-solid'
                                          />
                                          {item.name !== 'Mobile Number' && (
                                            <i
                                              onClick={() => {
                                                const tmpFields = values.fields.filter(
                                                  (el) => el.name !== item.name
                                                )
                                                setFieldValue('fields', tmpFields)
                                              }}
                                              className='bi bi-x'
                                            ></i>
                                          )}
                                        </div>
                                      </div>
                                    </li>
                                  ))}
                              </ReactSortable>
                            </Col>
                          </Row>
                        )}
                        <Row>
                          <Col md={4}>
                            <Select
                              id='fields'
                              name='fields'
                              options={fieldOptions.filter((opt) => {
                                if (values.fields.length === 0) {
                                  return true
                                }
                                const selectedValues = values.fields.map((field) => field.name)
                                if (!selectedValues.includes(opt.value)) {
                                  return true
                                }
                                return false
                              })}
                              value={{
                                label: '+ Add New Field',
                                value: '',
                              }}
                              onChange={(selectedOption) => {
                                if (selectedOption?.value.length === 0) {
                                  return
                                }
                                setFieldValue('fields', [
                                  ...values.fields,
                                  {name: selectedOption?.value, type: 'text'},
                                ])
                              }}
                              components={{
                                DropdownIndicator: () => null,
                                IndicatorSeparator: () => null,
                              }}
                              isDisabled={createLoading || updateLoading || isGroupLoading}
                              className='mb-3'
                              styles={customStyles}
                            />
                          </Col>
                        </Row>

                        <ErrorMessage name='fields' component='span' className='text-danger' />
                      </BSForm.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={8} className='mb-3'>
                      <BSForm.Group>
                        <BSForm.Label htmlFor='tags'>Tags</BSForm.Label>
                        <Select
                          id='tags'
                          name='tags'
                          options={toOptions(tags)}
                          value={toOptions(tags).filter((option) => {
                            const selectedValues = values.tags.map((tag) => tag.tagId)
                            if (selectedValues.includes(option.value)) {
                              return true
                            }
                            return false
                          })}
                          onChange={(selectedOption) => {
                            const selectedValues = selectedOption.map((field) => ({
                              tagId: field.value,
                              name: field.label,
                            }))
                            setFieldValue('tags', selectedValues)
                          }}
                          components={{MenuList}}
                          isMulti={true}
                          isDisabled={createLoading || updateLoading}
                          className='mb-3'
                        />
                        <div className='form-check form-check-sm form-check-custom form-check-solid d-flex align-items-center mb-4'>
                          <input
                            className='form-check-input widget-9-check me-3'
                            type='checkbox'
                            value={values.isUseDailyCaptureData ? 'yes' : 'no'}
                            checked={values.isUseDailyCaptureData}
                            onChange={() => {
                              setFieldValue('isUseDailyCaptureData', !values.isUseDailyCaptureData)
                            }}
                          />{' '}
                          Make a 24-hour tag
                          <OverlayTrigger trigger='hover' placement='right' overlay={InfoPopover}>
                            <i
                              className='bi bi-info-circle'
                              style={{
                                color: '#0d6efd',
                                marginLeft: '6px',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                verticalAlign: 'middle',
                              }}
                            ></i>
                          </OverlayTrigger>
                        </div>
                        <div className='form-check form-check-sm form-check-custom form-check-solid d-flex align-items-center mb-4'>
                          <input
                            className='form-check-input widget-9-check me-3'
                            type='checkbox'
                            value={values.isAutoDeleteContact ? 'yes' : 'no'}
                            checked={values.isAutoDeleteContact}
                            onChange={() => {
                              setFieldValue('isAutoDeleteContact', !values.isAutoDeleteContact)
                            }}
                          />{' '}
                          Delete contact after 48 hours
                          <OverlayTrigger
                            trigger='hover'
                            placement='right'
                            overlay={DeleteContactPopover}
                          >
                            <i
                              className='bi bi-info-circle'
                              style={{
                                color: '#0d6efd',
                                marginLeft: '6px',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                verticalAlign: 'middle',
                              }}
                            ></i>
                          </OverlayTrigger>
                        </div>
                        <div className='form-check form-check-sm form-check-custom form-check-solid d-flex align-items-center'>
                          <input
                            className='form-check-input widget-9-check me-3'
                            type='checkbox'
                            value={values.isMarketingMessageOptIn ? 'yes' : 'no'}
                            checked={values.isMarketingMessageOptIn}
                            onChange={() => {
                              setFieldValue(
                                'isMarketingMessageOptIn',
                                !values.isMarketingMessageOptIn
                              )
                            }}
                          />{' '}
                          Marketing Message Option
                          <OverlayTrigger
                            trigger='hover'
                            placement='right'
                            overlay={MarketingMessagePopover}
                          >
                            <i
                              className='bi bi-info-circle'
                              style={{
                                color: '#0d6efd',
                                marginLeft: '6px',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                verticalAlign: 'middle',
                              }}
                            ></i>
                          </OverlayTrigger>
                        </div>
                        <ErrorMessage name='tags' component='span' className='text-danger' />
                      </BSForm.Group>
                    </Col>
                  </Row>
                  {values.isMarketingMessageOptIn && (
                    <Row>
                      <Col md={12}>
                        <BSForm.Group>
                          <BSForm.Label htmlFor='marketingMessageOptin'>
                            Marketing Option Message
                          </BSForm.Label>
                          <Field
                            as='textarea'
                            id={'marketingMessageOptin'}
                            className={'form-control  form-control-solid mb-3'}
                            name='marketingMessageOptin'
                            type='text'
                            onChange={handleChange}
                            placeholder='Would you like to receive updates about future offers, events, and exclusive deals?'
                            disabled={createLoading || updateLoading}
                          />
                          <ErrorMessage
                            name='marketingMessageOptin'
                            component='span'
                            className='text-danger'
                          />
                        </BSForm.Group>
                      </Col>
                    </Row>
                  )}

                  {contactForm && contactForm._id && (
                    <Row>
                      <Col md={12}>
                        <BSForm.Group as={Row} className='mb-3'>
                          <Col className='d-flex flex-column justify-content-center'>
                            <BSForm.Label htmlFor='isActive'>Activate/Deactivate</BSForm.Label>
                          </Col>
                          <Col>
                            <BSForm.Check
                              type='switch'
                              id='isActive'
                              label={values.isActive ? ' YES' : 'NO'}
                              checked={values.isActive}
                              onChange={handleChange}
                              name='isActive'
                              className={`form-control ${
                                !values.isActive && 'checkbox-custom'
                              } no-background`}
                              disabled={createLoading || updateLoading}
                            />
                          </Col>
                        </BSForm.Group>
                        <ErrorMessage name='isActive' component='span' className='text-danger' />
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col md={12}>
                      <BSForm.Group>
                        <BSForm.Label htmlFor='name'>Redirect Url</BSForm.Label>
                      </BSForm.Group>
                      <Field
                        id={'redirectUrl'}
                        className={'form-control form-control-solid  mb-3'}
                        name='redirectUrl'
                        type='text'
                        onChange={handleChange}
                        disabled={createLoading || updateLoading}
                      />
                      <ErrorMessage name='redirectUrl' component='span' className='text-danger' />
                    </Col>
                  </Row>

                  <div className='modal-footer d-flex justify-content-end pe-0'>
                    <button
                      className='btn btn-md gray_bg_purple'
                      type='button'
                      onClick={handleClose}
                      disabled={createLoading || updateLoading}
                    >
                      Cancel
                    </button>

                    <button type='submit' className='btn btn-md btn-primary ml-2'>
                      {createLoading || updateLoading ? (
                        <Spinner size='sm' animation='grow' />
                      ) : (
                        'Save'
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <CreateTagModal
        show={showCreateTagModal}
        handleClose={() => setShowCreateTagModal(false)}
        handleCallback={(newTag) => {
          if (newTag) {
            fetchTagsData(null)
            setShowCreateTagModal(false)
          }
        }}
        mode={CreateTagModalMode.Tag}
      />
    </Modal>
  )
}

const InfoPopover = (
  <Popover id='daily-capture-popover'>
    <Popover.Header as='h3'>24 hours tag</Popover.Header>
    <Popover.Body>
      Each contact registered through this form will have the tag <code>{'{form-name}-today'}</code>{' '}
      added for 24 hours then it will be removed
    </Popover.Body>
  </Popover>
)

const DeleteContactPopover = (
  <Popover id='daily-capture-popover'>
    <Popover.Header as='h3'>48 hours Delete</Popover.Header>
    <Popover.Body>Anyone who comes into this list will be deleted after 48 hours</Popover.Body>
  </Popover>
)

const MarketingMessagePopover = (
  <Popover id='daily-capture-popover'>
    <Popover.Header as='h3'>Marketing Message Option</Popover.Header>
    <Popover.Body>Marketing message option</Popover.Body>
  </Popover>
)

const customStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: '#f3f0fb',
    borderColor: state.isFocused ? '#7950f2' : 'none',
    border: state.isFocused ? '#7950f2' : 'none',
    boxShadow: state.isFocused ? '0 0 0 1px #7950f2' : 'none',
    '&:hover': {
      border: '#7950f2',
      borderColor: '#7950f2',
    },
  }),
  singleValue: (provided: any, state: any) => ({
    ...provided,
    color: '#7950f2',
  }),
}
export default WriteModal
